
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Event } from "@/lib/types";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { SEO } from "@/components/SEO";
import { EventLoadingState } from "@/components/event/EventLoadingState";
import { EventErrorState } from "@/components/event/EventErrorState";
import { EventHeader } from "@/components/event/EventHeader";
import { EventTicketCTA } from "@/components/event/EventTicketCTA";
import { EventDetailsContent } from "@/components/event/EventDetailsContent";
import { URLRedirectHandler } from "@/components/URLRedirectHandler";

const EventOverview = () => {
  const { seoUrl } = useParams();
  const { toast } = useToast();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["event", seoUrl],
    queryFn: async () => {
      if (!seoUrl) {
        throw new Error("Event URL is required");
      }
      
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("seo_url", seoUrl)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Event not found");
      
      return data as Event;
    },
    retry: 1,
    enabled: !!seoUrl,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Unable to load event details. Please try again later.",
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return <EventLoadingState />;
  }

  if (error || !event) {
    return <EventErrorState />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <URLRedirectHandler />
      <SEO 
        dynamicData={{
          title: `${event.title} - Slay LGBTQIA+`,
          description: event.description || 'Join us for this exciting LGBTQIA+ event!',
          ogType: 'event',
          ogImage: event.image_url || 'https://slay.events/og-image.png'
        }}
      />
      <Header />
      <main className="flex-grow mx-auto px-4 pt-32 relative z-10 max-w-3xl w-full">
        <div className="w-full aspect-video mb-8 rounded-lg overflow-hidden shadow-lg">
          <img
            src={event.image_url || "/placeholder.svg"}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
        
        <EventHeader event={event} />
        <EventTicketCTA event={event} />
        <EventDetailsContent event={event} />
      </main>
      <Footer />
    </div>
  );
};

export default EventOverview;
